import { useCallback } from 'react';

import { formatSecondsToBrokenDownTime } from '@/utility/dateHelper';
import { t } from '@/utility/localization';
import {
  // MEMBER_ROUTES,
  getFolderItemPreviewLink
} from '@/utility/routesHelper';

import { STATUS_VIDEO_PROCESSING } from '@/contexts/LibrarySetupContext/constants';

import { showToast } from '@/components/common/ToastContainer';

import {
  FOLDER_ITEM_TYPE_AUDIO,
  FOLDER_ITEM_TYPE_FILE,
  FOLDER_ITEM_TYPE_IMAGE,
  FOLDER_ITEM_TYPE_LINK,
  FOLDER_ITEM_TYPE_SECTION,
  FOLDER_ITEM_TYPE_VIDEO
} from '../constants';
import {
  copyTextToClipboard,
  formatSize,
  getFileExtension,
  getFileFormat,
  openDownloadLinkInNewTab
} from '../utils';

const useFolderItemMetadataProps = (props) => {
  const {
    saveChangesOnFolderItem,
    activeCommunitySlug,
    selectedFolder,
    openDeleteItemModal
  } = props;
  const onCopyShareUrl = useCallback((text) => {
    copyTextToClipboard({ text });
    showToast({ text: t('link-copied') });
  }, []);

  const onThumbnailChange = useCallback(
    (folderItem, newThumbnailSrc) => {
      const payload = { thumbnail: newThumbnailSrc };
      saveChangesOnFolderItem(folderItem?._id, payload, folderItem);
    },
    [saveChangesOnFolderItem]
  );

  const getFormatTypeOfFile = useCallback((type) => {
    switch (type) {
      case 'VND.OPENXMLFORMATS-OFFICEDOCUMENT.SPREADSHEETML.SHEET':
        return 'XLSX';
      case 'VND.OPENXMLFORMATS-OFFICEDOCUMENT.WORDPROCESSINGML.DOCUMENT':
        return 'DOCX';
      case 'QUICKTIME':
        return 'MOV';
      case 'PLAIN':
        return 'TEXT';
      default:
        return type;
    }
  }, []);

  const handleCopyItemLink = useCallback(
    (item) => {
      const link = getFolderItemPreviewLink({
        communitySlug: activeCommunitySlug,
        productSlug: selectedFolder?.resourceSlug,
        folderItemId: item?._id,
        sectionItemId: item?.parentSectionId ?? '',
        fullUrl: true
      });
      onCopyShareUrl(link);
    },
    [activeCommunitySlug, onCopyShareUrl, selectedFolder?.resourceSlug]
  );

  const getMetadataFormat = useCallback(
    (item) => {
      if (item?.format) {
        let formatFile = getFileFormat(item?.format)?.toUpperCase();
        formatFile = getFormatTypeOfFile(formatFile);
        return formatFile;
      } else {
        let format = getFileExtension(item?.link)?.toUpperCase();
        // to fix data issue where link is saved as "undefined"
        return format === 'UNDEFINED' ? 'VIDEO' : format;
      }
    },
    [getFormatTypeOfFile]
  );

  const getItemProps = useCallback(
    (item) => {
      // Shared menu options data
      const DELETE_MENU_OPTION = {
        label: t('delete'),
        onClick: () => {
          openDeleteItemModal(item);
        },
        icon: 'trash-01',
        isDanger: true
      };

      const COPY_LINK_MENU_OPTION = {
        label: t('copy-link1'),
        onClick: () => handleCopyItemLink(item),
        icon: 'copy'
      };

      const DOWNLOAD_ASSET_MENU_OPTION = {
        label: t('download'),
        onClick: () => {
          const filelink = item?.link;

          if (filelink) openDownloadLinkInNewTab(filelink);
        },
        icon: 'download-cloud-01'
      };

      switch (item?.type) {
        case FOLDER_ITEM_TYPE_SECTION: {
          return {
            menuOptions: [DELETE_MENU_OPTION]
          };
        }
        case FOLDER_ITEM_TYPE_VIDEO:
          const isProcessing = item?.status === STATUS_VIDEO_PROCESSING;

          const DOWNLOAD_VIDEO_MENU_OPTION = item?.mp4Link
            ? {
                label: t('download'),
                onClick: () => {
                  const filelink = item?.mp4Link;
                  const filename = `${item?.title}.${getFileExtension(
                    item?.mp4Link
                  )?.toLowerCase()}`;

                  if (filelink && filename)
                    openDownloadLinkInNewTab(filelink, filename);
                },
                icon: 'download-cloud-01'
              }
            : null;
          return {
            metadataList: [
              getMetadataFormat(item) || 'VIDEO',
              formatSecondsToBrokenDownTime(item?.duration)
            ],
            menuOptions: isProcessing
              ? [DELETE_MENU_OPTION]
              : [
                  item?.shortUrl && COPY_LINK_MENU_OPTION,
                  item?.mp4Link && DOWNLOAD_VIDEO_MENU_OPTION,
                  DELETE_MENU_OPTION
                ]
          };
        case FOLDER_ITEM_TYPE_FILE:
          return {
            metadataList: [
              getMetadataFormat(item),
              formatSize(item?.size)
            ],
            menuOptions: [
              item?.shortUrl && COPY_LINK_MENU_OPTION,
              DOWNLOAD_ASSET_MENU_OPTION,
              DELETE_MENU_OPTION
            ]
          };
        case FOLDER_ITEM_TYPE_LINK:
          return {
            metadataList: ['URL', item?.link],
            menuOptions: [
              item?.shortUrl && COPY_LINK_MENU_OPTION,
              DELETE_MENU_OPTION
            ]
          };
        case FOLDER_ITEM_TYPE_IMAGE:
          return {
            // IMAGE SIZE
            metadataList: [
              getMetadataFormat(item),
              formatSize(item?.size)
            ],
            menuOptions: [
              item?.shortUrl && COPY_LINK_MENU_OPTION,
              DOWNLOAD_ASSET_MENU_OPTION,
              DELETE_MENU_OPTION
            ]
          };
        case FOLDER_ITEM_TYPE_AUDIO:
          return {
            metadataList: [
              getMetadataFormat(item),
              formatSecondsToBrokenDownTime(item?.duration)
            ],
            menuOptions: [
              item?.shortUrl && COPY_LINK_MENU_OPTION,
              DOWNLOAD_ASSET_MENU_OPTION,
              DELETE_MENU_OPTION
            ]
          };
      }
    },
    [getMetadataFormat, handleCopyItemLink, openDeleteItemModal]
  );

  return {
    getItemProps,
    onThumbnailChange
  };
};

export default useFolderItemMetadataProps;
